// 📋 TODO: Implement recording, transfer
import {
  BodyText,
  Button,
  ButtonGroup,
  Popover,
  Stack,
  Tag,
} from '@servicetitan/design-system';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  FC,
} from 'react';
import classNames from 'classnames';
import styles from './CallControls.module.css';
import CallControlKeypad from '@app/layouts/MainLayout/components/CallBar/components/CallControlKeypad';
import { inboxLocators } from '@locators/inbox';
import { CallControlsDivider } from './CallControlsDivider';
import { TransferringCall } from '../TransferringCall/TransferringCall';
import { CallControlsTooltip, TEXT_OFFSET } from './CallControlsTooltip';
import TransfersModule from '@modules/TransfersModule';
import { useWarmTransfer } from '@modules/TransfersModule/hooks/useWarmTransfer';
import { FeatureErrorBoundary } from '@components/ErrorBoundary';
import MicrophoneControl from './MicrophoneControl';
import {
  registerCallBarHeight,
  unregisterCallBarHeight,
} from '@app/layouts/MainLayout/components/CallBar/hooks/callBarHeightRegistry';

const IDENTITY_OVERFLOW_SIZE = 17;

interface CallControlsProps {
  task: any;
  show: boolean;
  pinned: boolean;
  established: boolean;
  hasSession: boolean;
  held: boolean;
  inTransfer: boolean;
  isAudioMuted: boolean;
  isVideoMuted: boolean;
  progress: boolean;
  identity: string; // remote identity, number or name that is displayed in the call bar
  terminating: boolean;
  formattedTime: string;
  isRecording: boolean;
  isRecordingDisabled: boolean;
  isRecordingLoading: boolean;
  recordingTooltip: string;
  phoneConnected: boolean;
  onHangup: VoidFunction;
  onAnswer: VoidFunction;
  onHold: VoidFunction;
  onMute: VoidFunction;
  onUnhold: VoidFunction;
  onUnmute: VoidFunction;
  onStartTimer: VoidFunction;
  toggleRecording: VoidFunction;
  onTransfer: VoidFunction;
}

// Positioning lives on the outer element and is the only thing that differs
// between the pinned and draggable bars. Kept out of the component body so the
// variant branching does not count against its cognitive complexity.
const outerClassName = (pinned: boolean, show: boolean) =>
  classNames(
    { 'd-n': !show },
    styles.stickyContainer,
    pinned ? styles.pinnedContainer : styles.draggableContainer,
    'z-overlay',
    { 'position-sticky': !pinned },
  );

const innerClassName = (pinned: boolean, warmTransferInProgress: boolean) =>
  classNames(
    `${styles.callControlsContainer} p-r-4 p-l-3 bg-neutral-400`,
    pinned
      ? styles.callControlsContainerPinned
      : styles.callControlsContainerDraggable,
    { 'p-t-2 p-b-3': warmTransferInProgress },
  );

const CallControls: FC<CallControlsProps> = ({
  task,
  show,
  pinned,
  established,
  held,
  isAudioMuted,
  identity,
  terminating,
  formattedTime,
  phoneConnected,
  onHangup,
  onAnswer,
  onHold,
  onMute,
  onUnhold,
  onUnmute,
  onStartTimer,
  isRecording,
  isRecordingDisabled,
  isRecordingLoading,
  recordingTooltip,
  toggleRecording,
  onTransfer,
}) => {
  const [transferring, setTransferring] = useState(false);
  const [isKeypadOpened, setIsKeypadOpened] = useState(false);
  const {
    warmTransferInProgress,
    warmTransferConnected,
    warmTransferConnectionStatus,
    disableButtonsWhenWarmTransferConnecting,
  } = useWarmTransfer(task?.attributes?.warmTransferContext);
  const canAnswer = terminating && !established;
  const canHangup = !terminating || (terminating && established);
  const isOverflow = identity?.length > IDENTITY_OVERFLOW_SIZE;

  const recordingAriaLabel = (() => {
    if (isRecordingDisabled) {
      return 'Recording button disabled';
    }
    if (isRecording) {
      return 'Stop Recording';
    }
    return 'Start Recording';
  })();

  useEffect(() => {
    onStartTimer();
    // Because it's in a useCallback in the hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const containerRef = useRef<HTMLDivElement>(null);
  const instanceId = useId();

  // When pinned, the bar is a fixed overlay at the bottom of the viewport.
  // MainLayout reserves matching bottom padding on the content containers
  // that wrap page/MFE content (see MainLayout.module.css) so nothing
  // renders underneath it — that reservation is entirely host-side, nothing
  // for page/MFE content to opt into. Publish the real measured height
  // (rather than a hardcoded one) so it stays correct across warm-transfer's
  // taller layout and other content reflow.
  //
  // CallBarManager mounts one CallControls per call (held calls included,
  // hidden via `show={false}`/display:none, and possibly more than one
  // shown at once), so no single instance may own the shared CSS var
  // directly — register/unregister this instance's own measured height
  // instead. CallBarManager is the sole owner that reduces the registry to
  // --call-bar-height, so one instance mounting or unmounting never
  // clobbers another still-visible instance's reservation.
  useLayoutEffect(() => {
    const el = containerRef.current;
    // Draggable bars float over content by design and must not reserve
    // bottom padding, so they never register a height. `pinned` must stay
    // in the dep array so a pinned -> unpinned transition runs the cleanup
    // below before the early return.
    if (!el || !show || !pinned) {
      return;
    }

    const updateHeight = () => {
      registerCallBarHeight(instanceId, el.offsetHeight);
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);

    return () => {
      observer.disconnect();
      unregisterCallBarHeight(instanceId);
    };
  }, [pinned, show, instanceId]);

  const handleTransfer = useCallback(() => {
    setTransferring(true);
    onTransfer();
  }, [onTransfer, setTransferring]);

  const handleCloseTransfer = useCallback(() => {
    setTransferring(false);
  }, [setTransferring]);

  const [isCompletingTransfer, setIsCompletingTransfer] = useState(false);

  const handleCompleteWarmTransfer = useCallback(async () => {
    setIsCompletingTransfer(true);
    try {
      await TransfersModule.completeWarmCallTransfer(
        task?.attributes?.warmTransferContext,
      );
    } finally {
      setIsCompletingTransfer(false);
    }
  }, [task?.attributes?.warmTransferContext]);

  return (
    <>
      <div
        ref={containerRef}
        className={outerClassName(pinned, show)}
        id={inboxLocators.callBar.callControls}
      >
        <Stack
          className={innerClassName(pinned, warmTransferInProgress)}
          alignItems={warmTransferInProgress ? 'flex-end' : 'center'}
          justifyContent='space-between'
        >
          {warmTransferInProgress ? (
            // Show both warm transfer and identifiers sections
            <Stack direction='column' justifyContent='flex-start' spacing={1}>
              {/* Warm Transfer Information */}
              <Stack direction='row' justifyContent='flex-start' spacing={2}>
                <Tag
                  className={`${styles.callsControlTag} fs-1`}
                  title='Transfer'
                >
                  Transfer
                </Tag>
                {warmTransferConnected ? (
                  <Tag
                    className={`${styles.callsControlTag} fs-1`}
                    title='Connected'
                    color='#444445'
                  >
                    Connected
                  </Tag>
                ) : (
                  <BodyText size='small' className='c-neutral-10'>
                    {warmTransferConnectionStatus}
                  </BodyText>
                )}
              </Stack>

              {/* Identifiers */}
              <Stack alignItems='center' className='p-r-2' spacing={2}>
                <CallControlsTooltip
                  text={isOverflow ? identity : ''}
                  offset={TEXT_OFFSET}
                >
                  <BodyText
                    id={inboxLocators.callBar.displayNameChecker}
                    className={classNames(styles.Identity, 'c-white fw-bol')}
                  >
                    {identity}
                  </BodyText>
                </CallControlsTooltip>
                {isRecording && (
                  <Tag
                    className={`${styles.callsControlTag} fs-1`}
                    color='critical'
                    icon='radio_button_checked'
                    title='Rec'
                  >
                    REC
                  </Tag>
                )}
                <BodyText
                  id={inboxLocators.callBar.durationLabel}
                  className='c-neutral-10'
                  size='small'
                >
                  {formattedTime}
                </BodyText>
                {held && (
                  <Tag
                    className={`${styles.callsControlTag} bg-neutral-200-i c-neutral-0-i fs-1`}
                    title='On Hold'
                  >
                    On Hold
                  </Tag>
                )}
              </Stack>
            </Stack>
          ) : (
            // Only render identifiers section since no warm transfer
            <Stack alignItems='center' className='p-r-2' spacing={2}>
              <CallControlsTooltip
                text={isOverflow ? identity : ''}
                offset={TEXT_OFFSET}
              >
                <BodyText
                  id={inboxLocators.callBar.displayNameChecker}
                  className={classNames(styles.Identity, 'c-white fw-bol')}
                >
                  {identity}
                </BodyText>
              </CallControlsTooltip>
              {isRecording && (
                <Tag
                  className={`${styles.callsControlTag} fs-1`}
                  color='critical'
                  icon='radio_button_checked'
                  title='Rec'
                >
                  REC
                </Tag>
              )}
              <BodyText
                id={inboxLocators.callBar.durationLabel}
                className='c-neutral-10'
                size='small'
              >
                {formattedTime}
              </BodyText>
              {held && (
                <Tag
                  className={`${styles.callsControlTag} bg-neutral-200-i c-neutral-0-i fs-1`}
                  title='On Hold'
                >
                  On Hold
                </Tag>
              )}
            </Stack>
          )}

          {/* Call Control Buttons */}
          <ButtonGroup className={classNames(styles.callControlsButtonGroup)}>
            <MicrophoneControl
              isAudioMuted={isAudioMuted}
              phoneConnected={phoneConnected}
              disabled={disableButtonsWhenWarmTransferConnecting}
              onMute={onMute}
              onUnmute={onUnmute}
            />

            {/* we would only show transfer call button if not in warm transfer */}
            {!warmTransferInProgress && (
              <CallControlsTooltip
                text='Transfer call'
                contentId={inboxLocators.callBar.transferBtnTooltip}
              >
                <Button
                  aria-label='Transfer'
                  className={
                    transferring
                      ? styles.callControlButtonActive
                      : styles.callControlButtonDefault
                  }
                  iconName='phone_forwarded'
                  onClick={handleTransfer}
                  id={inboxLocators.callBar.transferBtn}
                />
              </CallControlsTooltip>
            )}

            <CallControlsTooltip
              text={recordingTooltip}
              contentId={inboxLocators.callBar.pauseRecordingAudioCallTooltip}
            >
              <Button
                aria-label={recordingAriaLabel}
                className={
                  isRecording
                    ? styles.callControlButtonActiveCritical
                    : styles.callControlButtonDefault
                }
                id={inboxLocators.callBar.pauseRecordingAudioCallBtn}
                disabled={
                  isRecordingLoading ||
                  isRecordingDisabled ||
                  disableButtonsWhenWarmTransferConnecting
                }
                iconName='radio_button_checked'
                onClick={toggleRecording}
              />
            </CallControlsTooltip>

            {held ? (
              <CallControlsTooltip
                text='Resume Call'
                contentId={inboxLocators.callBar.resumeCallBtnTooltip}
              >
                <Button
                  aria-label='Resume Call'
                  className={styles.callControlButtonActive}
                  iconName='pause'
                  id={inboxLocators.callBar.resumeCallBtn}
                  onClick={onUnhold}
                  disabled={disableButtonsWhenWarmTransferConnecting}
                />
              </CallControlsTooltip>
            ) : (
              <CallControlsTooltip
                text='Hold'
                contentId={inboxLocators.callBar.placeCallOnHoldBtnTooltip}
              >
                <Button
                  aria-label='Hold'
                  className={styles.callControlButtonDefault}
                  iconName='pause'
                  id={inboxLocators.callBar.placeCallOnHoldBtn}
                  onClick={onHold}
                  disabled={disableButtonsWhenWarmTransferConnecting}
                />
              </CallControlsTooltip>
            )}
            <Popover
              className={classNames('bg-neutral-500', 'border-style-none')}
              wrapperClassName={styles.callControlPopperWrapper}
              open={isKeypadOpened}
              trigger={
                <CallControlsTooltip
                  text={isKeypadOpened ? 'Close Keypad' : 'Keypad'}
                >
                  <Button
                    className={classNames({
                      [styles.callControlButtonDefault]: !isKeypadOpened,
                      [styles.callControlButtonActive]: isKeypadOpened,
                    })}
                    id={inboxLocators.callBar.keypadBtn}
                    aria-label='Keypad'
                    iconName='dialpad'
                    disabled={disableButtonsWhenWarmTransferConnecting}
                    onClick={() => setIsKeypadOpened(!isKeypadOpened)}
                  />
                </CallControlsTooltip>
              }
            >
              <CallControlKeypad
                open={isKeypadOpened}
                onClose={() => setIsKeypadOpened(false)}
              />
            </Popover>
            {canHangup && warmTransferInProgress && (
              <CallControlsTooltip text='End Call'>
                <Button
                  id={inboxLocators.callBar.endCallButton}
                  aria-label='End Call'
                  color='red'
                  iconName='call_end'
                  onClick={onHangup}
                />
              </CallControlsTooltip>
            )}
            {canHangup && !warmTransferInProgress && <CallControlsDivider />}
            {canHangup && !warmTransferInProgress && (
              <CallControlsTooltip text='End Call'>
                <Button
                  id={inboxLocators.callBar.endCallButton}
                  aria-label='End Call'
                  color='red'
                  iconName='call_end'
                  onClick={onHangup}
                />
              </CallControlsTooltip>
            )}
            {canAnswer && (
              <CallControlsTooltip text='Answer'>
                <Button
                  aria-label='Answer'
                  color='primary'
                  iconName='phone'
                  onClick={onAnswer}
                />
              </CallControlsTooltip>
            )}
            {warmTransferInProgress && <CallControlsDivider />}
            {warmTransferInProgress && (
              <Button
                id={inboxLocators.callBar.completeTransferBtn}
                aria-label='Complete Warm Transfer'
                onClick={handleCompleteWarmTransfer}
                disabled={
                  disableButtonsWhenWarmTransferConnecting ||
                  isCompletingTransfer
                }
                className={styles.completeTransferButton}
              >
                Complete Transfer
              </Button>
            )}
          </ButtonGroup>
        </Stack>
      </div>
      {transferring && (
        <FeatureErrorBoundary featureName='Transferring Call' size='full'>
          <TransferringCall task={task} onClose={handleCloseTransfer} />
        </FeatureErrorBoundary>
      )}
    </>
  );
};

export default CallControls;
